import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  Avatar,
  Tooltip,
  InputAdornment,
  Stack,
  Tabs,
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Launch as LaunchIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  LocalShipping as ShippingIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  AccountBalance as BalanceIcon
} from '@mui/icons-material';
import { paymentAPI, orderAPI } from '../../services/api';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net';
const NET_TERMS_DAYS = 60;
const EARLY_PAY_WINDOW_DAYS = 2;

const OrderHistory = ({ currentUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [net60Only, setNet60Only] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = Open Balance, 1 = Paid
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch orders from API
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        // Use authenticated orderAPI instead of direct axios
        const response = await orderAPI.getOrders({
          page: 1,
          limit: 100
        });
        
        console.log('Fetched orders for client:', response);
        // orderAPI returns { success: true, data: [...] }
        setOrders(response.success ? response.data : []);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setOrders([]); // Empty array if API fails
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrders();
  }, []);

  // Calculate invoice details for orders
  const getInvoiceDetails = (order) => {
    // Ensure numeric values with proper fallbacks
    const totalAmount = Number(order.totalAmount || order.amount || 0);
    const paidAmount = Number(order.payments?.reduce((sum, payment) => 
      payment.status === 'CAPTURED' ? sum + Number(payment.amount || 0) : sum, 0) || 0);
    const balance = Math.max(0, totalAmount - paidAmount);
    
    // Calculate dates
    const orderDate = new Date(order.orderDate || order.createdAt);
    const net60Date = new Date(orderDate.getTime() + (NET_TERMS_DAYS * 24 * 60 * 60 * 1000));
    const discountDate = new Date(orderDate.getTime() + (EARLY_PAY_WINDOW_DAYS * 24 * 60 * 60 * 1000));
    
    // Check if eligible for 10% early payment discount (within 2 days) - Updated for EPL
    const now = new Date();
    const daysSinceOrder = Math.floor((now - orderDate) / (1000 * 60 * 60 * 24));
    const earlyPaymentEligible = now <= discountDate && balance > 0;
    const discountAmount = earlyPaymentEligible ? balance * 0.1 : 0;
    
    return {
      totalAmount: Number(totalAmount),
      paidAmount: Number(paidAmount), 
      balance: Number(balance),
      orderDate,
      net60Date,
      discountDate,
      earlyPaymentEligible,
      discountAmount: Number(discountAmount),
      daysSinceOrder
    };
  };

  // Handle Pay Now button click - Get real Hyfin payment link
  const handlePayNow = async (order) => {
    setPaymentLoading(true);
    
    try {
      console.log('🔗 Getting Hyfin payment link for order:', order.id);
      
      // Get the real payment link from Hyfin
      const response = await orderAPI.getPaymentLink(order.id);
      
      if (response.success && response.paymentLink) {
        console.log('✅ Got payment link:', response.paymentLink);
        
        // Open Hyfin payment page in new tab
        window.open(response.paymentLink, '_blank', 'noopener,noreferrer');
      } else {
        console.error('❌ Failed to get payment link:', response.error);
        setError(response.error || 'Unable to get payment link');
      }
    } catch (error) {
      console.error('❌ Error getting payment link:', error);
      setError(error.message || 'Unable to get payment link');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Removed handlePrintLabel - tracking number display only (no label printing needed)

  // Show all orders for now (will filter by userId after authentication)
  const providerOrders = useMemo(() => 
    orders,
    [orders]
  );

  // Render compact, card-based list on mobile for better readability
  const renderMobileOrders = () => (
    <Stack spacing={1.5} sx={{ p: 2 }}>
      {filteredOrders.map((order) => (
        <Paper key={order.id} variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
              {order.orderNumber || order.id}
            </Typography>
            <Chip
              icon={getStatusIcon(order.status)}
              label={getStatusLabel(order.status)}
              color={getStatusColor(order.status)}
              size="small"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {new Date(order.orderDate || order.createdAt).toLocaleDateString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Qty: {order.items ? order.items.reduce((sum, item) => sum + item.quantity, 0) : 0}
            </Typography>
            {order.items && order.items.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '100%' }}>
                {order.items[0].product?.name}
                {order.items.length > 1 ? ` +${order.items.length - 1} more` : ''}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Net 60: {getInvoiceDetails(order).net60Date.toLocaleDateString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Early Pay By: {getInvoiceDetails(order).discountDate.toLocaleDateString()}
            </Typography>
            {order.trackingNumber && (
              <Typography variant="body2" color="text.secondary">
                Tracking: {order.trackingNumber}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            {order.trackingNumber && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<LaunchIcon />}
                onClick={() => handleTrackOrder(order.trackingNumber)}
              >
                Track
              </Button>
            )}
            <Button size="small" variant="contained" onClick={() => handleViewOrder(order)}>
              Details
            </Button>
            {/* Hidden - payment methods now managed by Hyfin
            {getInvoiceDetails(order).balance > 0 && (
              <Button size="small" variant="outlined" onClick={() => navigate('/client/my-payments', { state: { order, invoiceDetails: getInvoiceDetails(order) } })}>
                Pay in My Payments
              </Button>
            )}
            */}
          </Stack>
        </Paper>
      ))}
      {filteredOrders.length === 0 && (
        <Typography align="center" color="text.secondary">
          No orders found matching your criteria
        </Typography>
      )}
    </Stack>
  );

  // Apply filters and search with tab-based payment filtering
  const filteredOrders = useMemo(() => {
    const baseFiltered = providerOrders.filter(order => {
      const matchesSearch = !searchTerm || 
        (order.orderNumber || order.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.user?.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.user?.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.items?.some(item => (item.product?.name || '').toLowerCase().includes(searchTerm.toLowerCase())));

      const matchesStatus = !statusFilter || order.status === statusFilter;

      const matchesDate = !dateFilter || (() => {
        const orderDate = new Date(order.orderDate || order.createdAt);
        const now = new Date();
        switch (dateFilter) {
          case 'last7days':
            return (now - orderDate) <= 7 * 24 * 60 * 60 * 1000;
          case 'last30days':
            return (now - orderDate) <= 30 * 24 * 60 * 60 * 1000;
          case 'last3months':
            return (now - orderDate) <= 90 * 24 * 60 * 60 * 1000;
          default:
            return true;
        }
      })();

      const matchesNet60 = !net60Only || order.paymentTerms === 'NET_60';
      return matchesSearch && matchesStatus && matchesDate && matchesNet60;
    });

    // Filter by payment status based on active tab
    if (activeTab === 0) {
      // Open Balance - unpaid or partially paid orders
      return baseFiltered.filter(order => {
        const { balance } = getInvoiceDetails(order);
        return balance > 0;
      });
    } else {
      // Paid - fully paid orders
      return baseFiltered.filter(order => {
        const { balance } = getInvoiceDetails(order);
        return balance <= 0;
      });
    }
  // Depend on filters including Net 60 toggle
  }, [providerOrders, searchTerm, statusFilter, dateFilter, activeTab, net60Only]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending':
      case 'PENDING': return 'warning';
      case 'Processing':
      case 'ASSIGNED':
      case 'PICKING': return 'info';
      case 'PACKED': return 'info';
      case 'Fulfilled':
      case 'SHIPPED': return 'success';
      case 'DELIVERED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };
  
  const getStatusLabel = (status) => {
    const labels = {
      'PENDING': 'Order Received',
      'ASSIGNED': 'Being Processed',
      'PICKING': 'Picking Items',
      'PACKED': 'Packed & Ready',
      'SHIPPED': 'Shipped',
      'DELIVERED': 'Delivered',
      'CANCELLED': 'Cancelled',
      // Legacy support
      'Pending': 'Order Received',
      'Processing': 'Processing',
      'Fulfilled': 'Fulfilled'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Pending': return <ScheduleIcon />;
      case 'Processing': return <WarningIcon />;
      case 'Fulfilled': return <CheckIcon />;
      default: return <ScheduleIcon />;
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOrderDetailOpen(true);
  };

  const handleTrackOrder = (trackingNumber) => {
    if (trackingNumber) {
      window.open(`https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`, '_blank');
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: color }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}15`, color: color }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        mb: 3,
        mt: { xs: '50px', md: 0 }, // push down on mobile to sit below hamburger
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        pl: { xs: '16px', md: 3 },
        transition: 'margin 0.2s ease'
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
          Order History
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
          View and manage all your orders
        </Typography>
      </Box>

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Orders"
            value={providerOrders.length}
            icon={<ShippingIcon />}
            color="#0d9488"
            subtitle="all time"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending"
            value={providerOrders.filter(o => o.status === 'Pending').length}
            icon={<ScheduleIcon />}
            color="#FFA73C"
            subtitle="awaiting processing"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Processing"
            value={providerOrders.filter(o => o.status === 'Processing').length}
            icon={<WarningIcon />}
            color="#17A2B8"
            subtitle="being prepared"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Fulfilled"
            value={providerOrders.filter(o => o.status === 'Fulfilled').length}
            icon={<CheckIcon />}
            color="#28A745"
            subtitle="shipped"
          />
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center"> 
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search orders, customers, or products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Processing">Processing</MenuItem>
                <MenuItem value="Fulfilled">Fulfilled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Date Range"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="">All Time</MenuItem>
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="last30days">Last 30 Days</MenuItem>
                <MenuItem value="last3months">Last 3 Months</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: '100%' }}>
                <Button variant="outlined" startIcon={<RefreshIcon />} fullWidth>
                  Refresh
                </Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} fullWidth>
                  Export
                </Button>
                <Button variant="outlined" startIcon={<PrintIcon />} fullWidth>
                  Print
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label="Net 60 Only"
                  color={net60Only ? 'primary' : 'default'}
                  variant={net60Only ? 'filled' : 'outlined'}
                  onClick={() => setNet60Only(v => !v)}
                />
                {net60Only && (
                  <Typography variant="body2" color="text.secondary">Showing orders with NET_60 terms</Typography>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Payment Status Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} aria-label="payment status tabs">
              <Tab 
                icon={<BalanceIcon />}
                label={`Open Balance (${providerOrders.filter(order => getInvoiceDetails(order).balance > 0).length})`}
                iconPosition="start"
              />
              <Tab 
                icon={<ReceiptIcon />}
                label={`Paid (${providerOrders.filter(order => getInvoiceDetails(order).balance <= 0).length})`}
                iconPosition="start"
              />
            </Tabs>
          </Box>
          
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {activeTab === 0 ? 'Open Balance' : 'Paid'} Orders ({filteredOrders.length})
            </Typography>
          </Box>
          
          {isMobile ? (
            renderMobileOrders()
          ) : (
            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Customer</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Product</TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Invoice Cost</TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Balance</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Invoice Date</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Due Date</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Tracking</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Payment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.map((order) => {
                    const invoiceDetails = getInvoiceDetails(order);
                    return (
                    <TableRow key={order.id} hover>
                      <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                        {order.orderNumber || order.id}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {order.user ? `${order.user.firstName} ${order.user.lastName}` : 'Customer'}
                          </Typography>
                          {order.user && (
                            <Typography variant="body2" color="text.secondary">
                              {order.user.email}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {order.items && order.items.length > 0 ? (
                          order.items.map((item, idx) => (
                            <Typography key={idx} variant="body2">
                              {item.product?.name || 'Product'} × {item.quantity}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }}>
                        ${invoiceDetails.totalAmount.toFixed(2)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color={invoiceDetails.balance > 0 ? 'error.main' : 'success.main'}>
                          ${invoiceDetails.balance.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2">{new Date(order.orderDate || order.createdAt).toLocaleDateString()}</Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2"><strong>Net 60:</strong> {invoiceDetails.net60Date.toLocaleDateString()}</Typography>
                        <Typography variant="body2"><strong>Early Pay By:</strong> {invoiceDetails.discountDate.toLocaleDateString()}</Typography>
                        {invoiceDetails.earlyPaymentEligible && (
                          <Typography variant="body2" color="success.main" sx={{ fontSize: '0.75rem' }}>
                            10% discount available through {invoiceDetails.discountDate.toLocaleDateString()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        {order.trackingNumber ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, mb: 0.5 }}>
                              {order.trackingNumber}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              href={
                                order.carrier === 'fedex_walleted' || order.carrier?.toLowerCase().includes('fedex')
                                  ? `https://www.fedex.com/fedextrack/?tracknumbers=${order.trackingNumber}`
                                  : order.carrier?.toLowerCase().includes('ups')
                                  ? `https://www.ups.com/track?tracknum=${order.trackingNumber}`
                                  : `https://tools.usps.com/go/TrackConfirmAction?tLabels=${order.trackingNumber}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ 
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                py: 0.25,
                                px: 1
                              }}
                            >
                              Track
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={getStatusIcon(order.status)}
                          label={getStatusLabel(order.status)}
                          color={getStatusColor(order.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {invoiceDetails.balance > 0 ? (
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={paymentLoading ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
                              onClick={() => handlePayNow(order)}
                              disabled={paymentLoading}
                              sx={{ minWidth: 100 }}
                            >
                              {paymentLoading ? 'Loading...' : 'Pay Now'}
                            </Button>
                            {/* Hidden - payment methods now managed by Hyfin
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate('/client/my-payments', { state: { order, invoiceDetails } })}
                            >
                              Pay in My Payments
                            </Button>
                            */}
                          </Stack>
                        ) : (
                          <Chip 
                            icon={<CheckIcon />}
                            label="Paid"
                            color="success"
                            size="small"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                  })}
                  {filteredOrders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No orders found matching your criteria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Modal */}
      <Dialog open={orderDetailOpen} onClose={() => setOrderDetailOpen(false)} maxWidth="md" fullWidth>
        {selectedOrder && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">Order Details - {selectedOrder.orderId}</Typography>
                <Chip
                  icon={getStatusIcon(selectedOrder.status)}
                  label={getStatusLabel(selectedOrder.status)}
                  color={getStatusColor(selectedOrder.status)}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Customer Information
                    </Typography>
                    <Typography variant="body2">
                      <strong>Customer:</strong> {selectedOrder.user ? `${selectedOrder.user.firstName} ${selectedOrder.user.lastName}` : 'Unknown'}
                    </Typography>
                    {selectedOrder.user?.email && (
                      <Typography variant="body2">
                        <strong>Email:</strong> {selectedOrder.user.email}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      <strong>Order Number:</strong> {selectedOrder.orderNumber}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Order Date:</strong> {new Date(selectedOrder.orderDate || selectedOrder.createdAt).toLocaleDateString()}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Order Information
                    </Typography>
                    {selectedOrder.items && selectedOrder.items.length > 0 ? (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          <strong>Products:</strong>
                        </Typography>
                        {selectedOrder.items.map((item, idx) => (
                          <Typography key={idx} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                            • {item.product?.name || 'Product'} × {item.quantity} - ${Number(item.totalPrice || 0).toFixed(2)}
                          </Typography>
                        ))}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Total Quantity:</strong> {selectedOrder.items.reduce((sum, item) => sum + item.quantity, 0)} unit(s)
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2">
                        <strong>Products:</strong> Loading...
                      </Typography>
                    )}
                    {selectedOrder.notes && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Notes:</strong> {selectedOrder.notes}
                      </Typography>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2">
                      <strong>Invoice Date:</strong> {new Date(selectedOrder.orderDate || selectedOrder.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Net 60 Date:</strong> {getInvoiceDetails(selectedOrder).net60Date.toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Early Pay By:</strong> {getInvoiceDetails(selectedOrder).discountDate.toLocaleDateString()}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Shipping Information
                    </Typography>
                    <Typography variant="body2">
                      <strong>Order Date:</strong> {new Date(selectedOrder.submissionDate).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Processing Time:</strong> {selectedOrder.estimatedProcessingTime}
                    </Typography>
                    {selectedOrder.trackingNumber && (
                      <Typography variant="body2">
                        <strong>Tracking Number:</strong> {selectedOrder.trackingNumber}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOrderDetailOpen(false)}>Close</Button>
              {selectedOrder.trackingNumber && (
                <Button
                  variant="contained"
                  startIcon={<LaunchIcon />}
                  onClick={() => handleTrackOrder(selectedOrder.trackingNumber)}
                >
                  Track Package
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default OrderHistory; 
